import React from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box } from '@mui/material';

const ProtectedRoute = ({ requiredRole, requireVisitorVerification }) => {
  const { isAuthenticated, loading, user, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Debug logging

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Explicit restriction: SuperAdmin cannot access User Management
  if (
    (isSuperAdmin() || user?.role === 'superadmin') &&
    location.pathname === '/user-management'
  ) {
    return <Navigate to="/tenant-management" replace />;
  }

  // Super admin can access any route
  if (isSuperAdmin() || user?.role === 'superadmin') {
    return <Outlet />;
  }

  // Handle visitor verification for checkout (QR code scanning)
  if (requireVisitorVerification && location.pathname === '/visitor-checkout') {
    try {
      // Get visitor data from QR code scan (URL parameters)
      const qrVisitorUserID = searchParams.get('visitorUserID');
      const qrVisitorEmail = searchParams.get('visitorEmail');

      // Verify that the current authenticated user matches the QR code data
      if (qrVisitorUserID && user?.uid !== qrVisitorUserID) {
        return <Navigate to="/visitor-dashboard" replace />;
      }

      if (qrVisitorEmail && user?.userEmailID !== qrVisitorEmail) {
        return <Navigate to="/visitor-dashboard" replace />;
      }

      // If verification passes, allow access
    } catch (error) {
      console.error('Error during visitor verification:', error);
      return <Navigate to="/visitor-dashboard" replace />;
    }
  }

  // If no specific role is required, allow authenticated users
  if (!requiredRole) {
    return <Outlet />;
  }

  // Handle host role access - allow host, tenant (backward compatibility), appadmin, and superadmin
  if (requiredRole === 'host') {
    if (user?.role === 'host' || user?.role === 'tenant' || user?.role === 'appadmin' || user?.role === 'facilityadmin' || isSuperAdmin()) {
      return <Outlet />;
    }
  }

  // Handle tenant role access (backward compatibility)
  if (requiredRole === 'tenant') {
    if (user?.role === 'host' || user?.role === 'tenant' || user?.role === 'appadmin' || isSuperAdmin()) {
      return <Outlet />;
    }
  }

  // Handle other specific role requirements (support array or string)
  if (Array.isArray(requiredRole)) {
    if (requiredRole.includes(user?.role)) {
      return <Outlet />;
    }
  } else if (user?.role === requiredRole) {
    return <Outlet />;
  }

  // Redirect to appropriate dashboard based on user's role
  const getRedirectPath = () => {
    if (isSuperAdmin() || user?.role === 'superadmin') return '/superadmin-dashboard';
    if (user?.role === 'appadmin') return '/tenant-admin-dashboard';
    if (user?.role === 'facilityadmin') return '/facility-dashboard';
    if (user?.role === 'host' || user?.role === 'tenant') return '/host-dashboard';
    if (user?.role === 'appoperator') return '/app-operator-dashboard';
    return '/visitor-dashboard';
  };

  return <Navigate to={getRedirectPath()} replace />;
};

export default ProtectedRoute;
